import React, { createContext, useContext, useState } from 'react';

interface ContentLoadingContextType {
  isContentLoading: boolean;
  contentLoadingText: string;
  showContentLoader: (text?: string) => void;
  hideContentLoader: () => void;
  setContentLoadingText: (text: string) => void;
}

const ContentLoadingContext = createContext<ContentLoadingContextType | undefined>(undefined);

export const useContentLoading = () => {
  const context = useContext(ContentLoadingContext);
  if (context === undefined) {
    throw new Error('useContentLoading must be used within a ContentLoadingProvider');
  }
  return context;
};

interface ContentLoadingProviderProps {
  children: React.ReactNode;
}

export const ContentLoadingProvider: React.FC<ContentLoadingProviderProps> = ({ children }) => {
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentLoadingText, setContentLoadingText] = useState('Loading...');

  const showContentLoader = (text: string = 'Loading...') => {
    setContentLoadingText(text);
    setIsContentLoading(true);
  };

  const hideContentLoader = () => {
    setIsContentLoading(false);
  };

  const updateContentLoadingText = (text: string) => {
    setContentLoadingText(text);
  };

  const value = {
    isContentLoading,
    contentLoadingText,
    showContentLoader,
    hideContentLoader,
    setContentLoadingText: updateContentLoadingText,
  };

  return (
    <ContentLoadingContext.Provider value={value}>
      {children}
    </ContentLoadingContext.Provider>
  );
};